import React, { useCallback, useEffect, useState } from "react";
import Layout from "../components/Layout";
import { API } from "../api";
import { toasts } from "../components/AppToast";
import Pagination from "../components/Pagination";

import * as XLSX from "xlsx";
import { FiCalendar, FiDownload, FiSearch, FiRefreshCw } from "react-icons/fi";

const formatDate = (date) => {
  if (!date) return "-";

  const d = new Date(date);

  if (Number.isNaN(d.getTime())) return date;

  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();

  return `${day}-${month}-${year}`;
};

export default function GenerateBookingsExcal() {
  const [bookings, setBookings] = useState([]);

  const [loading, setLoading] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  // Filter input values
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [bookingStatus, setBookingStatus] = useState("m");

  // Enables search button only when at least one filter has a value
  const hasFilter = Boolean(
    fromDate || toDate || (bookingStatus && bookingStatus !== "m")
  );

  // Controls whether search has been triggered
  const [searchFilters, setSearchFilters] = useState(null);

  const getPaymentStatus = (booking) => {
    const payStatus = String(booking?.pay_status ?? booking?.payStatus ?? "0");

    const paymentType = String(
      booking?.payment_type ?? booking?.paymentType ?? ""
    );

    if (payStatus === "1") {
      return {
        text: paymentType === "1" ? "Paid(P)" : "Paid(I)",
        className: "status-text text-active",
      };
    }

    return {
      text: "Unpaid",
      className: "status-text text-inactive",
    };
  };

  const fetchBookings = useCallback(async () => {
    if (!searchFilters) return;

    try {
      setLoading(true);

      const params = {
        page,
        limit,
      };

      if (searchFilters.fromDate) params.fromDate = searchFilters.fromDate;
      if (searchFilters.toDate) params.toDate = searchFilters.toDate;
      if (searchFilters.bookingStatus && searchFilters.bookingStatus !== "m") {
        params.bookingStatus = searchFilters.bookingStatus;
      }

      const response = await API.get("/online-booking/manage", { params });

      const result = response.data;

      if (result?.success) {
        const rows = Array.isArray(result.data) ? result.data : [];

        setBookings(rows);

        const total =
          Number(
            result.total ??
              result.pagination?.total ??
              result.total ??
              result.count ??
              rows.length
          ) || 0;

        setTotal(total);

        const pages =
          Number(
            result.totalPages ??
              result.pagination?.totalPages ??
              Math.ceil(total / limit)
          ) || 1;

        setTotalPages(pages);
      } else {
        setBookings([]);
        setTotal(0);
        setTotalPages(1);

        toasts.error(result?.message || "Unable to fetch bookings");
      }
    } catch (error) {
      console.error("Fetch bookings error:", error);

      setBookings([]);
      setTotal(0);
      setTotalPages(1);

      toasts.error(
        error.response?.data?.message || "Unable to connect to server"
      );
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchFilters]);

  // Load data after Search button click or pagination change
  useEffect(() => {
    if (searchFilters) {
      fetchBookings();
    }
  }, [fetchBookings, searchFilters]);

  // Search button
  const handleSearch = () => {
    if (fromDate && toDate && new Date(fromDate) > new Date(toDate)) {
      toasts.warning("From Date cannot be greater than To Date");
      return;
    }

    setPage(1);

    setSearchFilters({
      fromDate,
      toDate,
      bookingStatus,
    });
  };

  // Clear filters
  const handleClear = () => {
    setFromDate("");
    setToDate("");
    setBookingStatus("m");

    setSearchFilters(null);

    setBookings([]);
    setTotal(0);
    setTotalPages(1);
    setPage(1);
  };

  // Generate Excel
  const generateExcel = async () => {
    if (!searchFilters) {
      toasts.warning("Please search the bookings first");
      return;
    }

    try {
      setExcelLoading(true);

      const params = {
        page: 1,
        limit: 100000,
      };

      if (searchFilters.fromDate) params.fromDate = searchFilters.fromDate;
      if (searchFilters.toDate) params.toDate = searchFilters.toDate;
      if (searchFilters.bookingStatus && searchFilters.bookingStatus !== "m") {
        params.bookingStatus = searchFilters.bookingStatus;
      }

      const response = await API.get("/online-booking/manage", { params });

      const result = response.data;

      if (!result?.success) {
        toasts.error(result?.message || "Unable to generate Excel");
        return;
      }

      const rows = Array.isArray(result.data) ? result.data : [];

      if (rows.length === 0) {
        toasts.warning("No records available for selected filters");
        return;
      }

      const excelData = rows.map((booking) => {
        const bookingId =
          booking.bookingid ?? booking.bookingId ?? booking.id ?? "";

        const bookingNo =
          booking.fullbooking_no ??
          booking.fullbookingno ??
          booking.booking_no ??
          booking.bookingNo ??
          bookingId;

        const firstName = booking.online_firstname ?? booking.firstname ?? "";
        const lastName = booking.online_lastname ?? booking.lastname ?? "";
        const email = booking.online_emailid ?? booking.online_email ?? "";
        const pickDate = booking.online_pickdate ?? booking.pickdate;
        const bookingDate = booking.online_bookingdate ?? booking.bookingdate;

        const onlinePrice = Number(
          booking.online_onlineprice ?? booking.online_price ?? 0
        );

        const discount = Number(
          booking.Coup_DiscountAmt ??
            booking.coup_discountamt ??
            booking.discount ??
            0
        );

        const totalFare = onlinePrice - discount;
        const status = getPaymentStatus(booking);

        return {
          "Booking No.": bookingNo,
          Name: `${firstName} ${lastName}`.trim() || "-",
          "Email ID": email || "-",
          "Pick Up Date": formatDate(pickDate),
          "Booking Date": formatDate(bookingDate),
          "Total Fare": Number.isFinite(totalFare)
            ? totalFare.toFixed(2)
            : "0.00",
          Status: status.text,
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(excelData);

      worksheet["!cols"] = [
        { wch: 20 },
        { wch: 25 },
        { wch: 35 },
        { wch: 18 },
        { wch: 18 },
        { wch: 15 },
        { wch: 15 },
      ];

      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(workbook, worksheet, "Online Bookings");

      let fileName = "Online_Bookings";

      if (searchFilters.fromDate && searchFilters.toDate) {
        fileName += `_${searchFilters.fromDate}_to_${searchFilters.toDate}`;
      } else if (searchFilters.fromDate) {
        fileName += `_from_${searchFilters.fromDate}`;
      } else if (searchFilters.toDate) {
        fileName += `_to_${searchFilters.toDate}`;
      }

      if (searchFilters.bookingStatus === "1") {
        fileName += "_Paid";
      } else if (searchFilters.bookingStatus === "0") {
        fileName += "_Unpaid";
      }

      fileName += ".xlsx";

      XLSX.writeFile(workbook, fileName);

      toasts.success("Excel generated successfully");
    } catch (error) {
      console.error("Generate Excel error:", error);

      toasts.error(error.response?.data?.message || "Unable to generate Excel");
    } finally {
      setExcelLoading(false);
    }
  };

 // =========================================================
  // PAGINATION - PREVIOUS
  // =========================================================

  const handlePrevious = () => {
    if (
      loading ||
      page <= 1
    ) {
      return;
    }

    setPage(
      (prev) =>
        Math.max(
          1,
          prev - 1
        )
    );
  };

  // =========================================================
  // PAGINATION - NEXT
  // =========================================================

  const handleNext = () => {
    if (
      loading ||
      totalPages <= 0 ||
      page >= totalPages
    ) {
      return;
    }

    setPage(
      (prev) =>
        Math.min(
          totalPages,
          prev + 1
        )
    );
  };

  return (
    <Layout activePage="Generate Bookings Excel">
      

      <main className="dashboard-main">
        <div className="table-card">
          {/* Header */}
          <div className="table-card-head">
            <h3 className="table-card-title d-flex align-items-center">
              <FiCalendar size={26} className="me-2" />
              <span>Generate Bookings Excel</span>
              <span className="rec-count ms-1">{total} records</span>
            </h3>

            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="um-btn-primary btn-sm"
                onClick={generateExcel}
                disabled={excelLoading || !searchFilters}
              >
                {excelLoading ? (
                  <span className="spinner me-1" />
                ) : (
                  <FiDownload className="me-1" />
                )}
                {excelLoading ? "Generating..." : "Generate Excel"}
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="p-3 border-bottom bg-light bg-opacity-25">
            <div className="row g-3 align-items-end">
              {/* From Date */}
              <div className="col-xl-3 col-md-4 col-12">
                <label className="form-label fw-semibold small">
                  From Date
                </label>
                <input
                  type="date"
                  className="form-control"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>

              {/* To Date */}
              <div className="col-xl-3 col-md-4 col-12">
                <label className="form-label fw-semibold small">To Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>

              {/* Status */}
              <div className="col-xl-3 col-md-4 col-12">
                <label className="form-label fw-semibold small">Status</label>
                <select
                  className="form-select season-input"
                  value={bookingStatus}
                  onChange={(e) => setBookingStatus(e.target.value)}
                >
                  <option value="m">— All Status —</option>
                  <option value="1">Paid</option>
                  <option value="0">Unpaid</option>
                </select>
              </div>

              {/* Search & Clear Buttons */}
              <div className="col-xl-3 col-md-12 col-12">
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="um-btn-primary flex-grow-1"
                    onClick={handleSearch}
                    disabled={loading || !hasFilter}
                  >
                    <FiSearch className="me-1" />
                    {loading ? "Searching..." : "Search"}
                  </button>

                  <button
                    type="button"
                    className="um-btn-secondary flex-grow-1"
                    onClick={handleClear}
                  >
                    <FiRefreshCw className="me-1" />
                    Clear
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Table Section (Fixed Layout - No Scrollbar) */}
          <div>
            <table
              className="table table-striped table-hover table-bordered align-middle mb-0"
              style={{
                tableLayout: "fixed",
                width: "100%",
                fontSize: "12.5px",
              }}
            >
              <thead className="table-light">
                <tr className="um-table-head text-center">
                  <th style={{ width: "5%" }} className="text-center p-2">
                    #
                  </th>
                  <th style={{ width: "16%" }} className="text-center p-2">
                    Booking No.
                  </th>
                  <th style={{ width: "18%" }} className="text-center p-2">
                    Name
                  </th>
                  <th style={{ width: "23%" }} className="text-center p-2">
                    Email ID
                  </th>
                  <th style={{ width: "12%" }} className="text-center p-2">
                    Pick Up Date
                  </th>
                  <th style={{ width: "12%" }} className="text-center p-2">
                    Booking Date
                  </th>
                  <th style={{ width: "14%" }} className="text-center p-2">
                    Total Fare
                  </th>
                  <th style={{ width: "10%" }} className="text-center p-2">
                    Status
                  </th>
                </tr>
              </thead>

              <tbody>
                {/* Before Search */}
                {!searchFilters ? (
                  <tr>
                    <td colSpan={8} className="text-center text-muted py-5">
                      <div className="empty-icon">
                        <FiSearch size={22} />
                      </div>
                      <div className="empty-title">
                        Please select filters and click Search
                      </div>
                    </td>
                  </tr>
                ) : loading ? (
                  /* Loading Skeleton */
                  Array.from({ length: 6 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 8 }).map((_, j) => (
                        <td key={j} className="text-center p-2">
                          <div className="skeleton" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : bookings.length === 0 ? (
                  /* Empty State */
                  <tr>
                    <td colSpan={8} className="text-center py-5">
                      <div className="empty-icon">
                        <FiSearch size={22} />
                      </div>
                      <div className="empty-title">No records found</div>
                    </td>
                  </tr>
                ) : (
                  /* Records */
                  bookings.map((booking, index) => {
                    const bookingId =
                      booking.bookingid ??
                      booking.bookingId ??
                      booking.id ??
                      index;

                    const bookingNo =
                      booking.fullbooking_no ??
                      booking.fullbookingno ??
                      booking.booking_no ??
                      booking.bookingNo ??
                      bookingId;

                    const firstName =
                      booking.online_firstname ?? booking.firstname ?? "";

                    const lastName =
                      booking.online_lastname ?? booking.lastname ?? "";

                    const email =
                      booking.online_emailid ?? booking.online_email ?? "";

                    const pickDate =
                      booking.online_pickdate ?? booking.pickdate;

                    const bookingDate =
                      booking.online_bookingdate ?? booking.bookingdate;

                    const onlinePrice = Number(
                      booking.online_onlineprice ?? booking.online_price ?? 0
                    );

                    const discount = Number(
                      booking.Coup_DiscountAmt ??
                        booking.coup_discountamt ??
                        booking.discount ??
                        0
                    );

                    const totalFare = onlinePrice - discount;
                    const status = getPaymentStatus(booking);

                    return (
                      <tr key={bookingId} className="um-row">
                        {/* Sr No */}
                        <td className="text-center p-2 text-muted">
                          {(page - 1) * limit + index + 1}
                        </td>

                        {/* Booking No */}
                        <td
                          className="text-center p-2"
                          style={{ wordBreak: "break-word" }}
                        >
                          <strong>{bookingNo}</strong>
                        </td>

                        {/* Name */}
                        <td
                          className="text-center p-2"
                          style={{ wordBreak: "break-word" }}
                        >
                          {firstName || lastName
                            ? `${firstName} ${lastName}`.trim()
                            : "—"}
                        </td>

                        {/* Email */}
                        <td
                          className="text-center p-2"
                          style={{ wordBreak: "break-all" }}
                        >
                          {email || "—"}
                        </td>

                        {/* Pick Up Date */}
                        <td className="text-center p-2 text-nowrap">
                          {formatDate(pickDate)}
                        </td>

                        {/* Booking Date */}
                        <td className="text-center p-2 text-nowrap">
                          {formatDate(bookingDate)}
                        </td>

                        {/* Total Fare */}
                        <td className="text-center p-2 text-nowrap">
                          <strong>
                            {Number.isFinite(totalFare)
                              ? `€ ${totalFare.toFixed(2)}`
                              : "—"}
                          </strong>
                        </td>

                        {/* Status (Badge Only) */}
                        <td className="um-td text-center p-2">
                          <div className="status-wrap justify-content-center m-0">
                            <span
                              className={status.className}
                              style={{ fontSize: "11px", color: "red" }}
                            >
                              {status.text}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>

            {/* Pagination */}
            <Pagination
              page={page}
              totalPages={totalPages}
              total={total}
              limit={10}
              loading={loading}
              onPrevious={handlePrevious}
              onNext={handleNext}
            />
          </div>
        </div>
      </main>
   
    </Layout>
  );
}
